import { useMutation, useQuery } from '@tanstack/react-query';
import { bffApiClient } from '@api/bff/client';

export type WorkOrderRunCellRequest = {
  cell: string;
  scriptGlobalId: string;
  equipmentRef?: {
    companyName?: string | null;
    id?: string | null;
    globalId?: string | null;
  } | null;
  /** Function group ids to run for this cell. Omitted = all groups; [] is rejected (400). */
  selectedGroups?: string[];
  /** Composite functionId:label point ids to run for this cell. Omitted = all points. */
  selectedPoints?: string[];
};

export type RunStartMode = 'Fresh' | 'Warm';

export type CreateWorkOrderRunRequest = {
  jobNumber?: string;
  targetStationId: string;
  startedBy: string;
  cells?: WorkOrderRunCellRequest[];
  /** Robot power-up choice. Omitted/'Fresh' runs master setup once; 'Warm' skips it. */
  startMode?: RunStartMode;
  /**
   * Batch-scoped run (VOLTBOT-710/711): when set, the run's cells come from this batch (cross-job)
   * instead of a single jobNumber. Exactly one of batchId / jobNumber is used.
   */
  batchId?: string;
};

export type WorkOrderRun = {
  id: string;
  jobNumber: string | null;
  targetStationId: string | null;
  status: string;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
};

export type WorkOrderRunCellRef = {
  cell: string;
  scriptGlobalId: string;
  equipmentRef?: {
    companyName?: string | null;
    id?: string | null;
    globalId?: string | null;
  } | null;
  dcRuntimeId?: string | null;
};

export type WorkOrderRunDetail = {
  id: string;
  jobNumber: string | null;
  targetStationId: string | null;
  status: string;
  overallResult: string | null;
  measurementResults: string | null;
  eventLog: string | null;
  startedBy: string;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  cells: WorkOrderRunCellRef[] | null;
  dcRuntimeId?: string | null;
};

type WorkOrderRunPaginatedResponse = {
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  data: WorkOrderRunDetail[];
};

export const workOrderRunKeys = {
  byJobNumber: (jobNumber: string, page: number, pageSize: number) =>
    ['workOrderRuns', jobNumber, page, pageSize] as const
};

export const useWorkOrderRunsQuery = (
  jobNumber: string,
  page = 1,
  pageSize = 5
) => {
  return useQuery<WorkOrderRunPaginatedResponse, Error>({
    queryKey: workOrderRunKeys.byJobNumber(jobNumber, page, pageSize),
    queryFn: async () => {
      const response = await bffApiClient.get<WorkOrderRunPaginatedResponse>(
        '/api/runs',
        { params: { jobNumber, pageNumber: page, pageSize } }
      );
      return response.data;
    },
    enabled: !!jobNumber,
    staleTime: 10_000
  });
};

export const useCreateWorkOrderRun = () => {
  return useMutation<WorkOrderRun, Error, CreateWorkOrderRunRequest>({
    mutationFn: async payload => {
      const response = await bffApiClient.post<WorkOrderRun>(
        '/api/runs',
        payload
      );
      return response.data;
    }
  });
};
